import { toast } from "sonner";
import { api } from "./api";
import { labelsFor, svgFor } from "../components/VehicleDamageDiagram";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

// Builds a printable (save-as-PDF) document combining documents + inspection + photos
// for the public QR page. Arabic/RTL rendered natively by the browser.
export const printPublicCustody = (data, assetId) => {
  const w = window.open("", "_blank");
  if (!w) { toast.error("اسمح بالنوافذ المنبثقة لحفظ الملف"); return; }
  const urlFor = (fid) => api.publicCustodyFileUrl(assetId, fid);

  const docsRows = (data.documents || []).map((d) =>
    `<tr><th>${esc(d.label)}</th><td>${esc(d.value || "—")}</td><th>تاريخ الانتهاء</th><td>${esc(d.expiry || "—")}</td></tr>`).join("");

  const insp = data.inspection;
  let inspHTML = "";
  if (insp) {
    const checklist = insp.checklist || {};
    const LBL = labelsFor(insp.kind);
    const chk = Object.keys(LBL).map((k) =>
      `<div class="chk"><span class="${checklist[k] ? "ok" : "no"}">${checklist[k] ? "✓" : "✗"}</span> ${esc(LBL[k])}</div>`).join("");
    const damages = Array.isArray(insp.damages) ? insp.damages : [];
    const markers = damages.map((d, i) => `<div class="mk" style="left:${d.x}%;top:${d.y}%">${i + 1}</div>`).join("");
    const dmgList = damages.length
      ? `<ol class="dmglist">${damages.map((d) => `<li>${esc(d.note || "ضرر")}</li>`).join("")}</ol>`
      : `<div class="muted">لا توجد أضرار مسجّلة</div>`;
    const inspPhotos = (insp.photo_ids || []).map((fid) => `<img src="${urlFor(fid)}" />`).join("");
    inspHTML = `
      <div class="box">
        <h2>فحص التسليم</h2>
        ${insp.odometer != null && insp.odometer !== "" ? `<div class="odo">قراءة العدّاد: <b>${esc(String(insp.odometer))}</b> كم</div>` : ""}
        <div class="chkgrid">${chk}</div>
        <div class="dmgrow">
          <div class="diagram"><svg viewBox="0 0 200 320">${svgFor(insp.kind)}</svg>${markers}</div>
          <div class="dmgside"><div class="dmgttl">الأضرار المحددة</div>${dmgList}</div>
        </div>
        ${insp.note ? `<div class="note"><b>ملاحظة الفحص:</b> ${esc(insp.note)}</div>` : ""}
        ${inspPhotos ? `<div class="subttl">صور وقت الاستلام</div><div class="photos">${inspPhotos}</div>` : ""}
      </div>`;
  }

  const galleryHTML = (data.photos || []).length
    ? `<div class="box"><h2>صور المركبة</h2><div class="photos">${data.photos.map((fid) => `<img src="${urlFor(fid)}" />`).join("")}</div></div>`
    : "";

  const sigHTML = data.current_signature_id
    ? `<div class="box"><h2>توقيع المستلم</h2><img class="sig" src="${urlFor(data.current_signature_id)}" /></div>`
    : "";

  const chain = [...(data.assignments || [])].reverse().map((a) =>
    `<li><b>${esc(a.employee_name)}</b><br/><span class="muted">استلمها ${esc(a.received_at || "")}${a.returned_at ? ` — سلّمها ${esc(a.returned_at)}` : " — حتى الآن"}</span>${a.note ? `<br/><span class="muted">${esc(a.note)}</span>` : ""}</li>`).join("");

  w.document.write(`<!doctype html><html lang="ar" dir="rtl"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"/>
  <title>سجل العهدة - ${esc(data.identifier || data.type || "")}</title>
  <style>
    *{box-sizing:border-box} body{font-family:SaudiRiyal,"Segoe UI",Tahoma,Arial,sans-serif;color:#0f172a;margin:0;padding:24px;background:#fff}
    .head{background:#1d4ed8;color:#fff;border-radius:14px;padding:18px 20px;margin-bottom:16px}
    .head h1{margin:0;font-size:22px} .head .sub{margin-top:6px;font-size:13px;opacity:.9}
    .holder{background:#f1f5f9;border-radius:10px;padding:10px 14px;font-size:14px;margin-bottom:16px}
    .box{border:1px solid #e2e8f0;border-radius:10px;padding:14px;margin-bottom:16px;page-break-inside:avoid}
    .box h2{font-size:16px;margin:0 0 10px;color:#1e3a8a}
    table{width:100%;border-collapse:collapse;font-size:13px}
    th,td{border:1px solid #e2e8f0;padding:7px 9px;text-align:right} th{background:#f8fafc;color:#334155;width:22%;font-weight:700}
    .odo{font-size:13px;margin-bottom:8px;color:#334155}
    .chkgrid{display:grid;grid-template-columns:1fr 1fr;gap:4px 14px;font-size:12.5px;color:#334155}
    .chk .ok{color:#059669;font-weight:800} .chk .no{color:#e11d48;font-weight:800}
    .muted{color:#94a3b8;font-size:12px}
    .dmgrow{display:flex;gap:16px;align-items:flex-start;margin-top:12px}
    .diagram{position:relative;width:180px;flex:none} .diagram svg{width:180px;height:auto}
    .diagram .mk{position:absolute;transform:translate(-50%,-50%);width:20px;height:20px;border-radius:50%;background:#e11d48;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;border:2px solid #fff}
    .dmgside{flex:1} .dmgttl{font-weight:700;font-size:13px;color:#334155;margin-bottom:4px}
    .dmglist{margin:0;padding-inline-start:18px;font-size:12.5px;color:#334155;line-height:1.8}
    .note{margin-top:10px;font-size:13px;color:#334155;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:8px}
    .subttl{font-weight:700;font-size:13px;color:#334155;margin:12px 0 6px}
    .photos{display:flex;flex-wrap:wrap;gap:8px} .photos img{width:150px;height:112px;object-fit:cover;border:1px solid #e2e8f0;border-radius:8px}
    .sig{max-height:120px;border:1px solid #e2e8f0;border-radius:8px;padding:6px;background:#fff}
    ul.chainlist{list-style:none;margin:0;padding:0} ul.chainlist li{border-right:3px solid #cbd5e1;padding:2px 12px 10px;font-size:13px}
    .foot{text-align:center;color:#94a3b8;font-size:11px;margin-top:10px}
    @media print{body{padding:0}}
  </style></head><body>
    <div class="head">
      <h1>سجل العهدة — ${esc(data.type || "")}${data.identifier ? ` (${esc(data.identifier)})` : ""}</h1>
      <div class="sub">${esc(data.org_name || "")}${data.branch ? ` · الفرع: ${esc(data.branch)}` : ""}${data.chassis_number ? ` · الهيكل: ${esc(data.chassis_number)}` : ""}${data.color ? ` · اللون: ${esc(data.color)}` : ""}</div>
    </div>
    <div class="holder">بحوزة حالياً: <b>${esc(data.current_holder || "غير مُسلَّمة")}</b></div>
    ${docsRows ? `<div class="box"><h2>أوراق ومستندات المركبة</h2><table>${docsRows}</table></div>` : ""}
    ${inspHTML}
    ${galleryHTML}
    ${sigHTML}
    <div class="box"><h2>سلسلة التسليم</h2><ul class="chainlist">${chain}</ul></div>
    <div class="foot">تم الإنشاء بواسطة WorkZone — ${new Date().toLocaleDateString("en-GB")}</div>
    <script>
      window.onload = function(){
        var imgs = Array.prototype.slice.call(document.images);
        var done = 0, total = imgs.length;
        function go(){ setTimeout(function(){ window.print(); }, 250); }
        if(!total) return go();
        imgs.forEach(function(im){
          if(im.complete) { if(++done===total) go(); }
          else { im.onload = im.onerror = function(){ if(++done===total) go(); }; }
        });
        setTimeout(go, 3000);
      };
    </script>
  </body></html>`);
  w.document.close();
  w.focus();
};
